import {
  Default_Accent_From,
  Default_Accent_Text,
  Default_Accent_To,
  Default_Background_Primary_Color,
  Default_Background_Secondary_Color,
  Default_Button_Background_Color,
  Default_Button_Text_Color,
  getStudioAccent,
  IThemeAccent,
} from "../../../../../../common/infrastructure/utilities/colorUtility";
import {
  designStudioPresets,
  IDesignStudioColors,
  IDesignStudioPreset,
} from "../models/designStudioPresetModel";

export const designStudioColorKeys: (keyof IDesignStudioColors)[] = [
  "backgroundColorPrimary",
  "backgroundColorSecondary",
  "buttonBackgroundColor",
  "buttonTextColor",
  "accentColorFrom",
  "accentColorTo",
  "accentTextColor",
];

/** Planny's own look: the colours every studio starts with, and no accent of its own. */
export const designStudioDefaultColors: IDesignStudioColors = {
  backgroundColorPrimary: Default_Background_Primary_Color,
  backgroundColorSecondary: Default_Background_Secondary_Color,
  buttonBackgroundColor: Default_Button_Background_Color,
  buttonTextColor: Default_Button_Text_Color,
  accentColorFrom: "",
  accentColorTo: "",
  accentTextColor: "",
};

const normalize = (hex: string | null | undefined) =>
  (hex ?? "").trim().replace(/^#/, "").toLowerCase();

/** Two palettes are the same when all seven colours match, ignoring case and '#'. */
export const isSamePalette = (a: IDesignStudioColors, b: IDesignStudioColors) =>
  designStudioColorKeys.every((key) => normalize(a[key]) === normalize(b[key]));

export const findMatchingPreset = (
  colors: IDesignStudioColors,
): IDesignStudioPreset | null =>
  designStudioPresets.find((preset) => isSamePalette(preset.colors, colors)) ?? null;

/**
 * The accent a palette paints with, '#'-prefixed: its own when it has one, otherwise the
 * built-in purple → pink every studio without an accent keeps.
 */
export const resolvePaletteAccent = (colors: IDesignStudioColors): IThemeAccent =>
  getStudioAccent(colors) ?? {
    from: `#${Default_Accent_From}`,
    to: `#${Default_Accent_To}`,
    text: `#${Default_Accent_Text}`,
  };

/** Picks just the seven colours out of a larger object, e.g. the studio wizard form. */
export const pickDesignStudioColors = (
  source: Partial<Record<keyof IDesignStudioColors, string | null | undefined>>,
): IDesignStudioColors =>
  designStudioColorKeys.reduce((colors, key) => {
    colors[key] = source[key] ?? "";
    return colors;
  }, {} as IDesignStudioColors);
